import { pick } from 'lodash';
import type { Selector } from 'reselect';
import type { Optional } from 'utility-types';

import type { Website as CmsWebsite, ContentItem, Reference, WebsiteConfig } from '@swf/cms';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';

type KeysMatching<T, V> = { [K in keyof T]-?: T[K] extends V ? K : never }[keyof T];

interface Content {
  [key: ContentItem['uid']]: ContentItem;
}

type Website = Pick<
  CmsWebsite,
  | 'alternate_links'
  | 'canonical_link'
  | 'facebook_verification'
  | 'google_verification'
  | 'meta_tags'
  | 'robots_txt'
  | 'website_config'
  | 'site_footer'
>;

type ReferencedContent = KeysMatching<Website, Reference[]>;

export interface CmsState {
  content: Optional<Content>;
  website?: Website;
}

export interface CmsModel extends ModelConfig<CmsState> {
  reducers: typeof reducers;
  selectors: typeof selectors;
  effects: typeof effects;
}

interface CmsWebsiteV1Response {
  content: Content;
  website: Website;
}

const effects = {
  async fetchWebsite(payload: undefined, { dispatch, request, rootState }: EffectParams) {
    try {
      const {
        body: { content, website }
      } = await request<CmsWebsiteV1Response>({ service: '/api/v1-preview/cms/website' });
      dispatch.cms.setWebsite({
        content,
        website: pick(website, [
          'alternate_links',
          'canonical_link',
          'facebook_verification',
          'google_verification',
          'meta_tags',
          'robots_txt',
          'website_config',
          'site_footer'
        ])
      });
    } catch (err) {
      // do nothing
    }
  }
};

const reducers = {
  setWebsite: (state: CmsState, payload: CmsState): CmsState => payload
};

const selectors = (slice: Slicer<CmsState>, createSelector: SelectorCreator) => ({
  getContent() {
    return slice(({ content }) => content);
  },
  getWebsite() {
    return slice(({ website }) => website);
  },
  getAlternateLinks() {
    return createSelector(
      slice,
      (rootState) => (select.location.stripLocale as Selector<RootState, string>)(rootState),
      ({ website }, strippedPathname) =>
        (website?.alternate_links ?? [])
          .filter(({ link: { excluded_paths: excludedPaths } }) => {
            if (excludedPaths?.length) {
              const joinedExcludedPaths = excludedPaths
                .map((ep) => ep.path.replace(/(^\/)|(\/$)/g, ''))
                .join('|');
              const startWithExcludedPath = new RegExp(`^/(${joinedExcludedPaths})/.*`, 'g');
              return !startWithExcludedPath.test(strippedPathname);
            }

            return true;
          })
          .map(
            ({
              link: {
                locale_code: localeCode,
                base_url: baseUrl,
                replaced_path_start: replacePathStart
              }
            }) => {
              const linkPath =
                replacePathStart?.path_start_to_replace && replacePathStart?.new_path_start
                  ? strippedPathname.replace(
                      new RegExp(`^${replacePathStart.path_start_to_replace}`),
                      replacePathStart.new_path_start
                    )
                  : strippedPathname;

              return {
                localeCode,
                url: `${baseUrl}${linkPath}`
              };
            }
          )
    );
  },
  getCanonicalLink() {
    return slice(({ website }) => website?.canonical_link);
  },
  getMetaTags() {
    return slice(({ website }) => website?.meta_tags ?? []);
  },
  getReferencedContent() {
    return createSelector(
      (_: RootState, property: ReferencedContent) => property,
      (rootState) => select.cms.getWebsite(rootState),
      (rootState) => select.cms.getContent(rootState),
      (property, website, content) => {
        if (!website) return;
        const refs = website[property];
        if (!refs) return;
        const [ref] = refs;
        if (!ref) return;
        return content[ref.uid];
      }
    );
  },
  getLaunchDate() {
    return (rootState: RootState) => {
      const websiteConfig = select.cms.getReferencedContent(rootState, 'website_config') as
        | WebsiteConfig
        | undefined;
      return websiteConfig?.isp_launch_date;
    };
  }
});

const site: CmsModel = {
  state: {
    content: {}
  },
  reducers,
  effects,
  selectors
};

export default site;
